<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		background-color-class="bg-color-bg-tertiary"
		:transparent-header="true"
		:transparent-header-dark-text="true"
	>
		<AssetPageHero class="pt-300" :hero="heroDetails" />
		<ClientReturnsPerformanceSection
			:related-articles-section="mappedRelatedArticles"
			:is-related-articles-loading="relatedArticlesLoading"
			@open-modal="openModal(returnsDisclosureModalProps)"
		/>
		<IncomeThroughDividendsSection @open-modal="openModal" />
		<HistoricalStabilitySection @open-modal="openModal(annualReturnsDisclosureModalProps)" />
		<ClientPerformanceByFundSection />
		<CtaSection theme="dark" />
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { annualReturnsDisclosureModalProps } from '@constants/annual-returns-data';
import { appCloudfrontPath } from '@constants';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import ClientPerformanceByFundSection from '@components/marketing-site/client-returns/client-performance-by-fund-section.vue';
import ClientReturnsPerformanceSection from '@components/marketing-site/client-returns/client-returns-performance-section.vue';
import CtaSection from '@components/marketing-site/cta-section.vue';
import DisclaimerSimple from '@components/disclaimer/disclaimer-simple.vue';
import { getClientReturnsRelatedArticles } from '@sanity/graphql/client-returns';
import HistoricalStabilitySection from '@components/marketing-site/client-returns/historical-stability-section.vue';
import IncomeThroughDividendsSection from '@components/marketing-site/client-returns/income-through-dividends-section.vue';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';
import { MarketingImageHero } from 'types/layout';
import { SanityRelatedArticlesSection } from 'types/sanity/blog-education-center';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';

const appStore = useAppStore();

const heroDetails: MarketingImageHero = {
	backgroundColor: 'bg-color-bg-tertiary',
	description:
		'Diversifying outside of the public market not only helps in managing risk through non-correlated investments, but also opens up an opportunity for potentially higher returns and strategic investment advantages, thus playing a crucial role in a comprehensive investment strategy.',
	heading: 'Advisory Client returns',
	headingSize: 'heading-xl',
	title: 'Diversification <br />that delivers',
	imageUrl: `${appCloudfrontPath}/images/marketing-site/hero-images/hero-img-client-returns.avif`
};

const returnsDisclosureModalProps = {
	content: [
		'This chart is intended to provide transparency for prospective clients, and other interested parties, into how the accounts of our clients have performed.',
		'Individual results and performance may and will vary greatly. Past performance is not indicative of future results. All investments involve risks and may result in partial or total loss.',
		'Figures in this chart represent the cumulative time-weighted returns, calculated using the <a href="https://www.investopedia.com/terms/m/modifieddietzmethod.asp#:~:text=The%20modified%20Dietz%20method%20is,a%20specified%20period%20of%20time." target="_blank">Modified Dietz method</a>, of client accounts of Fundrise Advisors over the period beginning on the first day that such account became a shareholder in one or more of the sponsored programs, and ending yesterday, or in cases where the account was fully redeemed, the last day that such account held shares. This data captures inputs specific to each individual client, including fees charged, redemption penalties, and investment decisions by the applicable individual such as plan selected, dividend reinvestment, auto-investment, and manual investments.',
		'The performance calculations do not include the performance of project dependent notes or short-term notes, or investments in Rise Companies itself (the “Fundrise iPO”), the Fundrise Opportunity Fund, the Innovation Fund, or the Opportunistic Credit Fund.',
		'The “Client Track Record” chart does not represent the individual performance of any specific investment program (e.g., an eREIT) sponsored by Rise Companies.'
	],
	centeredContent: false
};

const relatedArticlesLoading = ref(true);

const relatedArticles = ref<SanityRelatedArticlesSection | null>(null);

const mappedRelatedArticles = computed((): SanityRelatedArticlesSection => {
	return {
		sectionHeading: relatedArticles.value?.sectionHeading ?? '',
		relatedArticles: mapInvestorUpdateArticleTeasers(relatedArticles.value?.relatedArticles ?? [])
	};
});

function openModal(modalProps: object): void {
	appStore.updateCurrentModal({
		modal: DisclaimerSimple,
		props: modalProps
	});
}

async function setup(): Promise<void> {
	useHead({
		title: 'Client Returns',
		meta: [
			{ property: 'og:title', content: 'Client Returns' },
			{ name: 'description', content: 'Explore client performance over time' },
			{ property: 'og:description', content: 'Explore client performance over time' }
		]
	});
	try {
		relatedArticles.value = await getClientReturnsRelatedArticles();
		relatedArticlesLoading.value = false;
	} catch {}
}
setup();
</script>
